/* Shopify Customer Account API, Public Web client + PKCE.
 * No Admin token, client secret, refresh token, profile or address is persisted.
 * Only the pending PKCE transaction (including the customer-ID binding) uses
 * this tab's sessionStorage for up to ten minutes.
 * API access tokens live in memory and are discarded on navigation/sign-out.
 */
(() => {
  'use strict';
  const ADDRESS_FIELDS = 'id firstName lastName company address1 address2 city country countryCode province zoneCode zip phoneNumber formatted(withName: true)';
  const PROFILE_QUERY = `query VitalAccount($after: String) { customer { id firstName lastName defaultAddress { id } addresses(first: 50, after: $after) { nodes { ${ADDRESS_FIELDS} } pageInfo { hasNextPage endCursor } } } }`;
  const NAME_MUTATION = 'mutation VitalName($input: CustomerUpdateInput!) { customerUpdate(input: $input) { customer { id firstName lastName } userErrors { field message } } }';
  const PREFERENCES_QUERY = 'query VitalEmailPreferences { customer { id emailAddress { marketingState } } }';
  const EMAIL_SUBSCRIBE = 'mutation VitalEmailSubscribe { customerEmailMarketingSubscribe { emailAddress { marketingState } userErrors { field message } } }';
  const EMAIL_UNSUBSCRIBE = 'mutation VitalEmailUnsubscribe { customerEmailMarketingUnsubscribe { emailAddress { marketingState } userErrors { field message } } }';
  const CREATE_MUTATION = `mutation VitalAddressCreate($address: CustomerAddressInput!, $defaultAddress: Boolean) { customerAddressCreate(address: $address, defaultAddress: $defaultAddress) { customerAddress { ${ADDRESS_FIELDS} } userErrors { field message } } }`;
  const UPDATE_MUTATION = `mutation VitalAddressUpdate($addressId: ID!, $address: CustomerAddressInput, $defaultAddress: Boolean) { customerAddressUpdate(addressId: $addressId, address: $address, defaultAddress: $defaultAddress) { customerAddress { ${ADDRESS_FIELDS} } userErrors { field message } } }`;
  const inputKeys = ['firstName','lastName','company','address1','address2','city','countryCode','zoneCode','zip','phoneNumber'];
  const customerId = value => /^gid:\/\/shopify\/Customer\/(\d+)$/.exec(String(value || ''))?.[1] || '';
  function httpsURL(value) {
    const u = new URL(value);
    if (u.protocol !== 'https:' || u.username || u.password || u.hash) throw new Error('Shopify returned an invalid connection address.');
    return u.href;
  }
  function base64url(bytes) {
    return btoa(String.fromCharCode(...new Uint8Array(bytes))).replace(/\+/g,'-').replace(/\//g,'_').replace(/=+$/,'');
  }
  class AccountClient {
    constructor({clientId, expectedCustomerId, origin, accountPath, storage, fetcher = fetch, cryptoAPI = crypto, now = Date.now, guard = async () => {}}) {
      this.clientId = clientId;
      this.expectedCustomerId = String(expectedCustomerId || '');
      this.origin = new URL(origin).origin;
      const redirect = new URL(accountPath, this.origin);
      if (redirect.origin !== this.origin || !redirect.pathname.includes('/pages/')) throw new Error('Invalid account page address.');
      redirect.search = ''; redirect.hash = '';
      this.redirectUri = redirect.href;
      this.storage = storage; this.fetcher = fetcher.bind(globalThis); this.crypto = cryptoAPI; this.now = now; this.guard = guard;
      this.key = 'vital-account-pkce:' + clientId;
      this.token = ''; this.expiresAt = 0; this.verified = false;
    }
    random() { return base64url(this.crypto.getRandomValues(new Uint8Array(32))); }
    async request(url, options = {}) {
      let response;
      try { response = await this.fetcher(url, {...options, cache:'no-store', credentials:'omit', referrerPolicy:'no-referrer', signal:AbortSignal.timeout(20000)}); }
      catch { throw new Error('We couldn’t connect to Shopify. Your entered details have not been cleared. Please try again.'); }
      if (!response.ok) {
        if (response.status === 401) { this.clear(); throw new Error('Your editing session has expired. Close this form and select Edit again to reconnect.'); }
        throw new Error('We couldn’t connect to Shopify. Your entered details have not been cleared. Please try again.');
      }
      return response.json();
    }
    async discover() {
      if (this.endpoints) return this.endpoints;
      const [auth, api] = await Promise.all([
        this.request(this.origin + '/.well-known/openid-configuration'),
        this.request(this.origin + '/.well-known/customer-account-api')
      ]);
      // Discovery is fetched only from this storefront, never from query parameters.
      this.endpoints = {authorize:httpsURL(auth.authorization_endpoint), token:httpsURL(auth.token_endpoint), graphql:httpsURL(api.graphql_api)};
      return this.endpoints;
    }
    async authorize(intent) {
      if (!this.expectedCustomerId || !this.clientId) throw new Error('Sign in to your store account before editing.');
      const endpoints = await this.discover();
      const state = this.random(), verifier = this.random();
      const safeIntent = /^(profile|preferences|todos|website-settings|system-logbook|add|edit:\d+)$/.test(intent) ? intent : 'profile';
      const transaction = {state, verifier, redirectUri:this.redirectUri, intent:safeIntent, customerId:this.expectedCustomerId, createdAt:this.now()};
      try { this.storage.setItem(this.key, JSON.stringify(transaction)); }
      catch { throw new Error('Allow session storage in this browser to securely connect account editing.'); }
      const challenge = base64url(await this.crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)));
      const url = new URL(endpoints.authorize);
      const params = {client_id:this.clientId, scope:'openid email customer-account-api:full', response_type:'code', redirect_uri:this.redirectUri, state, code_challenge:challenge, code_challenge_method:'S256'};
      for (const [key,value] of Object.entries(params)) url.searchParams.set(key,value);
      return url.href;
    }
    async complete(callback) {
      const url = new URL(callback);
      let pending;
      try { pending = JSON.parse(this.storage.getItem(this.key) || 'null'); } catch { pending = null; }
      // Consume before any network call so callback reload/replay cannot reuse it.
      this.storage.removeItem(this.key);
      const age = pending ? this.now() - pending.createdAt : Infinity;
      if (!pending || !Number.isFinite(age) || age < 0 || age > 600000 || url.searchParams.getAll('state').length !== 1 || url.searchParams.get('state') !== pending.state || pending.redirectUri !== this.redirectUri || url.origin + url.pathname !== this.redirectUri || pending.customerId !== this.expectedCustomerId || !this.expectedCustomerId) {
        throw new Error('This editing connection has expired or could not be verified. Select Edit again to reconnect.');
      }
      if (url.searchParams.has('error')) throw new Error('Account editing was not connected. You can try again when you’re ready.');
      const code = url.searchParams.get('code');
      if (!code || url.searchParams.getAll('code').length !== 1) throw new Error('Shopify did not return a valid editing connection.');
      const endpoints = await this.discover();
      const body = new URLSearchParams({grant_type:'authorization_code', client_id:this.clientId, code, redirect_uri:this.redirectUri, code_verifier:pending.verifier});
      const tokens = await this.request(endpoints.token, {method:'POST',headers:{'Content-Type':'application/x-www-form-urlencoded'},body});
      if (typeof tokens.access_token !== 'string' || !tokens.access_token || !Number.isFinite(tokens.expires_in) || tokens.expires_in <= 0) throw new Error('Shopify did not return a valid editing session.');
      this.token = tokens.access_token;
      this.expiresAt = this.now() + tokens.expires_in * 1000;
      try {
        const data = await this.graphql(PROFILE_QUERY, {after:null}, false);
        if (customerId(data.customer?.id) !== this.expectedCustomerId) throw new Error('This connection belongs to a different customer. Sign out, then sign in with the same account before editing.');
        this.verified = true;
        return {intent:pending.intent, customer:data.customer};
      } catch (error) { this.clear(); throw error; }
    }
    ready() { return this.verified && Boolean(this.token) && this.expiresAt > this.now() + 15000; }
    clear(pending = true) {
      this.token = ''; this.expiresAt = 0; this.verified = false;
      if (pending) { try { this.storage.removeItem(this.key); } catch { /* memory is already cleared */ } }
    }
    async graphql(query, variables, requireVerified = true) {
      if (!this.token || this.expiresAt <= this.now() || (requireVerified && !this.verified)) { this.clear(); throw new Error('Your editing session has expired. Close this form and select Edit again to reconnect.'); }
      if (requireVerified) await this.guard();
      const endpoints = await this.discover();
      const result = await this.request(endpoints.graphql, {method:'POST', headers:{'Content-Type':'application/json',Authorization:this.token}, body:JSON.stringify({query,variables})});
      if (result.errors?.length) throw new Error('Shopify couldn’t complete that request. Check the account connection and try again.');
      if (!result.data) throw new Error('Shopify did not return account details. Please try again.');
      return result.data;
    }
    async profile(after = null) { return (await this.graphql(PROFILE_QUERY,{after})).customer; }
    async emailPreference() {
      const data = await this.graphql(PREFERENCES_QUERY,{});
      if (customerId(data.customer?.id) !== this.expectedCustomerId) { this.clear(); throw new Error('Your account connection has changed. Reload this page before continuing.'); }
      return data.customer.emailAddress;
    }
    async updateEmailPreference(subscribe) {
      if (typeof subscribe !== 'boolean') throw new Error('Choose an email preference before saving.');
      const data = await this.graphql(subscribe ? EMAIL_SUBSCRIBE : EMAIL_UNSUBSCRIBE,{});
      return this.result(subscribe ? data.customerEmailMarketingSubscribe : data.customerEmailMarketingUnsubscribe).emailAddress;
    }
    async updateName(input) {
      const data = await this.graphql(NAME_MUTATION,{input:{firstName:input.firstName,lastName:input.lastName}});
      return this.result(data.customerUpdate).customer;
    }
    result(payload) {
      if (!payload) throw new Error('Shopify did not confirm the change. Check your details before retrying.');
      if (payload.userErrors?.length) throw new Error(payload.userErrors.map(e=>e.message).join('\n'));
      return payload;
    }
    async saveAddress(input, id, makeDefault) {
      const address = Object.fromEntries(inputKeys.map(key=>[key,String(input[key] || '').trim()]));
      // An empty optional phone must explicitly clear the old value on updates.
      address.phoneNumber = address.phoneNumber || null;
      const variables = {address,defaultAddress:makeDefault ? true : undefined};
      if (id) variables.addressId = id;
      const data = await this.graphql(id ? UPDATE_MUTATION : CREATE_MUTATION,variables);
      return this.result(id ? data.customerAddressUpdate : data.customerAddressCreate).customerAddress;
    }
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {AccountClient,base64url,customerId};
  if (typeof document === 'undefined') return;

  function install(root) {
    if (root.dataset.apiInstalled) return;
    root.dataset.apiInstalled = 'true';
    const callback = new URL(location.href);
    const phoneKey='vital-todo-phone-intent',isCallback=callback.searchParams.has('code')||callback.searchParams.has('error');
    let phoneMode=callback.searchParams.get('todo')==='1';
    try{if(isCallback&&sessionStorage.getItem(phoneKey)==='1')phoneMode=true;}catch{}
    let phone=null,phoneHost=null;
    if(phoneMode){
      const fragment=document.querySelector('[data-va-phone-template]')?.content.cloneNode(true);
      if(fragment){phone=fragment.querySelector('[data-va-phone]');document.body.append(fragment);phoneHost=phone.querySelector('[data-phone-host]');document.title='To-do · Vitoitems';
        for(const element of [...document.body.children])if(element!==phone&&!['SCRIPT','STYLE','LINK'].includes(element.tagName))element.inert=true;
        const manifest=document.createElement('link');manifest.rel='manifest';manifest.href='https://vitoitems-sourcing.niyosbrands.workers.dev/sourcing/todo.webmanifest';document.head.append(manifest);
        const icon=document.createElement('link');icon.rel='apple-touch-icon';icon.href='https://vitoitems-sourcing.niyosbrands.workers.dev/sourcing/todo-icon.png';document.head.append(icon);
        for(const [name,content]of [['apple-mobile-web-app-capable','yes'],['apple-mobile-web-app-title','To-do'],['theme-color','#173f67']]){const meta=document.createElement('meta');meta.name=name;meta.content=content;document.head.append(meta);}
        const account=phone.querySelector('[data-phone-account]');account.addEventListener('click',()=>{try{sessionStorage.removeItem(phoneKey);}catch{};});
        const signin=phone.querySelector('[data-phone-signin]');if(signin){const url=new URL(signin.href,location.origin),returnTo=new URL(location.href);for(const key of ['code','state','error','error_description','iss'])returnTo.searchParams.delete(key);returnTo.searchParams.set('todo','1');url.searchParams.set('return_to',returnTo.pathname+returnTo.search);signin.href=url.href;}
        window.addEventListener('offline',()=>{phone.querySelector('[data-phone-status]').textContent=phone.dataset.offline;});
      }
    }
    if (callback.searchParams.has('code') || callback.searchParams.has('error')) {
      const clean = new URL(callback.href);
      for (const key of ['code','state','error','error_description','iss']) clean.searchParams.delete(key);
      history.replaceState(null,'',clean.pathname + clean.search + clean.hash);
    }
    if (root.dataset.signedIn !== 'true' || root.dataset.editingEnabled !== 'true') return;
    const select = q => root.querySelector(q);
    const status = phone?phone.querySelector('[data-phone-status]'):select('[data-va-status]');
    const profileForm = select('[data-va-profile-form]');
    const addressForm = select('[data-va-address-form]');
    const preferencesForm = select('[data-va-preferences-form]');
    const moreButton = select('[data-va-more-addresses]');
    const message = (node, text, error = false, success = false) => { if (node) { node.textContent = text; node.dataset.error = String(error); node.dataset.success = String(success && !error); } };
    let client;
    async function assertStorefrontSession() {
      const response = await fetch(client.redirectUri,{cache:'no-store',credentials:'same-origin',signal:AbortSignal.timeout(20000)});
      if (!response.ok) throw new Error('We couldn’t verify your current sign-in. Your entered details have not been cleared. Please try again.');
      const html = new DOMParser().parseFromString(await response.text(),'text/html');
      const session = html.querySelector('[data-vital-account]');
      if (session?.dataset.signedIn !== 'true' || session.dataset.customerId !== client.expectedCustomerId) {
        client.clear();
        throw new Error('Your store sign-in has changed. Reload this page and sign in again before editing.');
      }
    }
    try { client = new AccountClient({clientId:root.dataset.clientId,expectedCustomerId:root.dataset.customerId,origin:location.origin,accountPath:root.dataset.accountPath,storage:sessionStorage,guard:assertStorefrontSession}); }
    catch { message(status,'Account editing is unavailable in this browser.',true); return; }
    let customer = null, addresses = [], pageInfo = {}, activeAddressId = '', busy = false;
    let todoWidget=null,todoDirty=false,todoTimer,storeCheckedAt=0,storeCheck=null,todoPrime=null,todoModule=null;
    let adminWidget=null,adminModule=null,adminTimer,adminDirty=false;
    function closeAdminTools(){clearTimeout(adminTimer);adminWidget?.destroy();adminWidget=null;adminDirty=false;}
    function loadAdminTools(){return adminModule||(adminModule=import('https://vitoitems-sourcing.niyosbrands.workers.dev/sourcing/account-tools.js?v=1').catch(e=>{adminModule=null;throw e;}));}
    async function openAdminTools(kind){
      if(!select('[data-va-admin-tool="'+kind+'"]'))throw Error('Workspace tools are unavailable for this account.');
      await fetchTodo('whoami');
      if(!adminWidget){const module=await loadAdminTools();adminWidget=module.mountAccountTools(select('[data-va-admin-host]'),fetchTodo,value=>{adminDirty=value;});adminTimer=setTimeout(closeAdminTools,Math.max(0,client.expiresAt-Date.now()-15000));}
      await adminWidget.open(kind);
    }

    function loadTodoModule(){return todoModule||(todoModule=import('https://vitoitems-sourcing.niyosbrands.workers.dev/sourcing/account-todos.js?v=phone1').catch(e=>{todoModule=null;throw e;}));}
    if(select('[data-va-todos]'))loadTodoModule().catch(()=>{});
    function closeTodos(){closeAdminTools();if(phone){message(status,phone.dataset.locked);phone.querySelector('[data-phone-connect]')?.removeAttribute('hidden');phone.querySelector('[data-phone-refresh]').hidden=true;}clearTimeout(todoTimer);todoWidget?.destroy();todoWidget=null;todoDirty=false;todoPrime=null;storeCheckedAt=0;}
    async function checkTodoStore(){if(Date.now()-storeCheckedAt<5000)return;if(!storeCheck)storeCheck=assertStorefrontSession().then(()=>{storeCheckedAt=Date.now();}).finally(()=>{storeCheck=null;});return storeCheck;}
    async function fetchTodo(action,data={}){
      if(!['whoami','todo-list','todo-save','todo-delete','website-settings-context','website-settings-products','website-settings-catalog','website-settings-images','website-settings-load','website-settings-save','logbook-list','logbook-record','logbook-knowledge-list','logbook-knowledge-save'].includes(action))throw Error('Invalid workspace action.');
      if(!client.ready()){closeTodos();throw Error('Your task session expired. Select Open to-do again.');}
      try{await checkTodoStore();}catch(e){closeTodos();throw e;}
      const r=await fetch('https://vitoitems-sourcing.niyosbrands.workers.dev/sourcing/api/'+action,{method:'POST',headers:{'Content-Type':'application/json',Authorization:'Customer '+client.token},body:JSON.stringify(data),cache:'no-store',credentials:'omit',signal:AbortSignal.timeout(25000)});
      let d;try{d=await r.json();}catch{throw Error('The task change could not be confirmed. Reopen To-do before retrying.');}
      if(!r.ok||!d.ok){if([401,403].includes(r.status)){closeTodos();message(status,d.error||'Staff access is unavailable.',true);}throw Error(d.error||'Task request failed.');}return d;
    }
    function primeTodos(){if(!select('[data-va-todos]')||!client.ready())return;const promise=fetchTodo('todo-list');todoPrime={promise,until:Date.now()+10000};promise.catch(()=>{if(todoPrime?.promise===promise)todoPrime=null;});}
    async function todoRequest(action,data={}){if(action==='todo-list'&&todoPrime&&todoPrime.until>Date.now()&&client.ready()){const p=todoPrime.promise;todoPrime=null;return p;}todoPrime=null;return fetchTodo(action,data);}
    async function openTodos(){
      if(!todoWidget){const module=await loadTodoModule();todoWidget=module.mountAccountTodos(phoneHost||select('[data-va-todo-host]'),todoRequest,value=>{todoDirty=value;},{inline:!!phone});todoTimer=setTimeout(()=>closeTodos(),Math.max(0,client.expiresAt-Date.now()-15000));}
      await todoWidget.open();if(phone&&todoWidget){phone.querySelector('[data-phone-connect]').hidden=true;phone.querySelector('[data-phone-refresh]').hidden=false;message(status,phone.dataset.ready);}
    }
    window.addEventListener('focus',()=>{storeCheckedAt=0;todoPrime=null;});
    window.addEventListener('beforeunload',event=>{if(todoDirty||adminDirty){event.preventDefault();event.returnValue='';}});
    window.addEventListener('pagehide',closeTodos);
    root.querySelector('[data-va-signout]')?.addEventListener('click',closeTodos);
    let emailState = '';
    const emailStates = {SUBSCRIBED:'Subscribed to promotional emails.',PENDING:'Your subscription is awaiting email confirmation.',NOT_SUBSCRIBED:'Not subscribed to promotional emails.',UNSUBSCRIBED:'Unsubscribed from promotional emails.',INVALID:'Email preferences are unavailable for this address.',REDACTED:'Email preferences are unavailable for this account.'};
    function paintEmail(email) {
      if (!email || !Object.hasOwn(emailStates,email.marketingState)) throw new Error('Your email preference could not be confirmed. Close this form and try again.');
      emailState = email.marketingState;
      preferencesForm.elements.emailMarketing.checked = ['SUBSCRIBED','PENDING'].includes(emailState);
      preferencesForm.elements.emailMarketing.disabled = ['INVALID','REDACTED'].includes(emailState);
      select('[data-va-email-state]').textContent = emailStates[emailState];
    }
    const shortAddressId = id => String(id || '').split('/').pop();
    function show(form) {
      profileForm.hidden = form !== profileForm; addressForm.hidden = form !== addressForm;
      if (preferencesForm) preferencesForm.hidden = form !== preferencesForm;
      if (!form) return;
      message(form.querySelector('[data-va-form-status]'),'');
      form.hidden = false;
      form.scrollIntoView({behavior:'auto',block:'center'});
      form.querySelector('input')?.focus({preventScroll:true});
    }
    function renderAddresses() {
      const list = select('[data-va-address-list]');
      list.replaceChildren();
      select('[data-va-initial-address-note]')?.remove();
      if (!addresses.length) {
        const empty = document.createElement('p'); empty.className = 'va-note'; empty.textContent = 'You haven’t saved an address yet.'; list.append(empty);
      }
      for (const address of addresses) {
        const card = document.createElement('div'); card.className = 'va-address-card';
        if (address.id === customer.defaultAddress?.id) { const label = document.createElement('p'); label.className = 'va-default'; label.textContent = 'Default address'; card.append(label); }
        const body = document.createElement('div'); body.className = 'va-address';
        for (const line of address.formatted || []) { const p = document.createElement('p'); p.textContent = line; body.append(p); }
        const edit = document.createElement('button'); edit.type = 'button'; edit.className = 'va-button'; edit.dataset.vaEditAddress = shortAddressId(address.id); edit.textContent = 'Edit address';
        card.append(body,edit); list.append(card);
      }
      moreButton.hidden = !pageInfo.hasNextPage;
      // Keep the Liquid total when only the first page is loaded.
      if (!pageInfo.hasNextPage) select('[data-va-address-count]').textContent = String(addresses.length);
    }
    function applyProfile(data, append = false) {
      customer = data;
      addresses = append ? [...addresses,...data.addresses.nodes] : data.addresses.nodes;
      pageInfo = data.addresses.pageInfo;
      select('[data-va-name]').textContent = [data.firstName,data.lastName].filter(Boolean).join(' ') || 'Not provided';
      select('[data-va-greeting]').textContent = data.firstName ? `Hello, ${data.firstName}.` : 'Welcome back.';
      if (profileForm.hidden) {
        profileForm.elements.firstName.value = data.firstName || '';
        profileForm.elements.lastName.value = data.lastName || '';
      }
      renderAddresses();
    }
    async function open(intent, trigger) {
      if (busy) return;
      busy = true; if (trigger) trigger.disabled = true;
      try {
        if (!client.ready()) {
          message(status,'Connecting securely to your account…');
          if(phone)try{sessionStorage.setItem(phoneKey,'1');}catch{}
          location.assign(await client.authorize(intent));
          return;
        }
        if (intent === 'todos') {await openTodos(); return;}
        if (['website-settings','system-logbook'].includes(intent)) {await openAdminTools(intent);return;}
        if (intent === 'preferences' && preferencesForm) {
          paintEmail(await client.emailPreference());
          show(preferencesForm); return;
        }
        if (intent === 'profile') {
          profileForm.elements.firstName.value = customer.firstName || '';
          profileForm.elements.lastName.value = customer.lastName || '';
          show(profileForm); return;
        }
        const wantedId = intent.startsWith('edit:') ? intent.slice(5) : '';
        let address = null;
        if (wantedId) {
          address = addresses.find(a=>shortAddressId(a.id) === wantedId);
          while (!address && pageInfo.hasNextPage) {
            applyProfile(await client.profile(pageInfo.endCursor),true);
            address = addresses.find(a=>shortAddressId(a.id) === wantedId);
          }
          if (!address) throw new Error('That address is no longer available. Please refresh your account.');
        }
        activeAddressId = address?.id || '';
        addressForm.reset();
        for (const key of inputKeys) {
          const field = addressForm.elements[key];
          const value = address?.[key] || (key === 'countryCode' ? root.dataset.country : '');
          if (key === 'countryCode' && value && !Array.from(field.options).some(o=>o.value===value)) field.add(new Option(address?.country || value,value));
          field.value = value;
        }
        const isDefault = Boolean(address && customer.defaultAddress?.id === address.id);
        addressForm.elements.defaultAddress.checked = isDefault;
        addressForm.elements.defaultAddress.disabled = isDefault;
        select('[data-va-address-form-title]').textContent = address ? 'Edit address' : 'Add address';
        show(addressForm);
      } catch (error) { message(status,error.message,true); }
      finally { busy = false; if (trigger) trigger.disabled = false; }
    }
    root.addEventListener('click', event => {
      const button = event.target.closest('button');
      if (!button) return;
      if (button.matches('[data-va-todos]')) open('todos',button);
      if (button.matches('[data-va-admin-tool]')) open(button.dataset.vaAdminTool,button);
      if (button.matches('[data-va-edit-profile]')) open('profile',button);
      if (button.matches('[data-va-edit-preferences]')) open('preferences',button);
      if (button.matches('[data-va-add-address]')) open('add',button);
      if (button.matches('[data-va-edit-address]')) open('edit:'+button.dataset.vaEditAddress,button);
      if (button.matches('[data-va-cancel]')) { show(null); message(status,''); }
    });
    function busyForm(form,on) { form.querySelectorAll('button').forEach(button=>button.disabled=on); form.setAttribute('aria-busy',String(on)); }
    preferencesForm?.addEventListener('submit',async event => {
      event.preventDefault(); if (busy) return;
      const output = preferencesForm.querySelector('[data-va-form-status]');
      if (!emailState || ['INVALID','REDACTED'].includes(emailState)) {message(output,'This email preference cannot be changed here.',true);return;}
      const subscribe = preferencesForm.elements.emailMarketing.checked;
      if (subscribe === ['SUBSCRIBED','PENDING'].includes(emailState)) {message(output,emailStates[emailState] + ' No changes to save.');return;}
      busy = true; busyForm(preferencesForm,true); preferencesForm.elements.emailMarketing.disabled = true; message(output,'Saving…');
      try {
        paintEmail(await client.updateEmailPreference(subscribe));
        message(output,emailState === 'PENDING' ? 'Check your email to confirm your subscription.' : 'Your email preference has been saved.',false,emailState !== 'PENDING');
      } catch (error) {message(output,error.message,true);}
      finally {busy=false;busyForm(preferencesForm,false);preferencesForm.elements.emailMarketing.disabled=['INVALID','REDACTED'].includes(emailState);}
    });
    profileForm.addEventListener('submit', async event => {
      event.preventDefault(); if (busy) return; busy = true; busyForm(profileForm,true);
      const output = profileForm.querySelector('[data-va-form-status]'); message(output,'Saving…');
      try {
        const saved = await client.updateName({firstName:profileForm.elements.firstName.value.trim(),lastName:profileForm.elements.lastName.value.trim()});
        customer = {...customer,...saved};
        select('[data-va-name]').textContent = [saved.firstName,saved.lastName].filter(Boolean).join(' ') || 'Not provided';
        select('[data-va-greeting]').textContent = saved.firstName ? `Hello, ${saved.firstName}.` : 'Welcome back.';
        message(output,'Your name has been saved.',false,true);
      } catch (error) { message(output,error.message,true); }
      finally { busy = false; busyForm(profileForm,false); }
    });
    addressForm.addEventListener('submit', async event => {
      event.preventDefault(); if (busy) return; busy = true; busyForm(addressForm,true);
      const output = addressForm.querySelector('[data-va-form-status]'); message(output,'Saving…');
      let saved = false;
      try {
        const input = Object.fromEntries(inputKeys.map(key=>[key,addressForm.elements[key].value]));
        const address = await client.saveAddress(input,activeAddressId,addressForm.elements.defaultAddress.checked);
        if (!address?.id) throw new Error('Shopify did not confirm the saved address. Check your addresses before retrying.');
        saved = true;
        // Switch to update mode immediately so a refresh failure cannot cause a duplicate create.
        activeAddressId = address.id;
        select('[data-va-address-form-title]').textContent = 'Edit address';
        applyProfile(await client.profile());
        message(output,'Your address has been saved.',false,true);
      } catch (error) { message(output,saved ? 'Your address was saved, but the list could not refresh. Reload the page to see it.' : error.message,true); }
      finally { busy = false; busyForm(addressForm,false); }
    });
    moreButton.addEventListener('click',async () => {
      if (busy || !pageInfo.hasNextPage) return; busy = true; moreButton.disabled = true;
      try { applyProfile(await client.profile(pageInfo.endCursor),true); }
      catch (error) { message(status,error.message,true); }
      finally { busy = false; moreButton.disabled = false; }
    });
    root.querySelector('[data-va-signout]')?.addEventListener('click',()=>client.clear());
    if(phone){phone.querySelector('[data-phone-connect]')?.addEventListener('click',event=>open('todos',event.currentTarget));phone.querySelector('[data-phone-refresh]').addEventListener('click',()=>{if(!todoDirty||confirm(phone.dataset.discard))open('todos');});}
    // Preserve only the pending PKCE transaction while navigating to Shopify.
    window.addEventListener('pagehide',()=>client.clear(false));
    if (callback.searchParams.has('code') || callback.searchParams.has('error')) {
      message(status,'Connecting account editing…');
      client.complete(callback.href).then(async result => {
        applyProfile(result.customer); message(status,''); primeTodos();if(phone){const clean=new URL(location.href);clean.searchParams.set('todo','1');history.replaceState(null,'',clean.pathname+clean.search);try{sessionStorage.removeItem(phoneKey);}catch{}} await open(result.intent);
      }).catch(error => message(status,error.message,true));
    } else if(phone&&select('[data-va-todos]')) {open('todos',phone.querySelector('[data-phone-connect]'));}
  }
  document.querySelectorAll('[data-vital-account]').forEach(install);
  document.addEventListener('shopify:section:load',event=>event.target.querySelectorAll('[data-vital-account]').forEach(install));
})();




